class LazerVariantSelector extends HTMLElement {
  constructor() {
    super();
    console.group('LazerVariantSelector Initialization');

    this.productButtons = this.querySelectorAll('[data-product-handle]');
    this.variantButtons = this.querySelectorAll('.subscription-variant');
    this.totalPriceElements = this.querySelectorAll('.total-price');
    this.compareAtPriceElements = this.querySelectorAll('.compare-at-price');
    this.currentVariantId = this.dataset.currentVariantId;
    this.currentVariantPrice = parseInt(this.dataset.currentVariantPrice, 10);
    this.currentVariantCompareAtPrice = parseInt(this.dataset.currentVariantCompareAtPrice, 10);
    this.currentVariantSavings = Math.max(0, this.currentVariantCompareAtPrice - this.currentVariantPrice);
    this.currency = this.dataset.currency || 'USD';
    this.enableSubscriptionAddons = this.dataset.enableSubscriptionAddons === 'true';
    this.enableBfSubDeal = this.dataset.enableBfSubDeal === 'true';
    this.subscriptionDiscountAmount = parseInt(this.dataset.subscriptionDiscountAmount, 10);
    this.isAddingToCart = false;
    this.clearCartHandles = [
      'katalyst-training-system-gen4',
      'katalyst-training-system-gen3',
      'katalyst-gen4-traninig-system-x-hypervolt-2-pro',
    ];
    this.standardAddToCartButton = this.querySelector('[data-standard-add-to-cart]');
    console.log('Initialized clearCartHandles:', this.clearCartHandles);

    try {
      // Parse products data from base64
      const productsData = this.dataset.products;
      if (productsData) {
        this.products = JSON.parse(atob(productsData));
      }
    } catch (error) {
      console.error('Error parsing data:', error);
    }

    // Store bound handlers
    this.handleVariantSelectionBound = this.handleVariantSelection.bind(this);
    this.handleProductSelectionBound = this.handleProductSelection.bind(this);
  }

  connectedCallback() {
    console.log('Connected callback');

    this.variantButtons.forEach((button) => {
      button.addEventListener('click', this.handleVariantSelectionBound);
    });

    this.productButtons.forEach((button) => {
      button.addEventListener('click', this.handleProductSelectionBound);
    });

    if (this.standardAddToCartButton) {
      this.standardAddToCartButton.addEventListener('click', this.handleSimpleAddToCartBound);
    }
  }

  disconnectedCallback() {
    this.variantButtons.forEach((button) => {
      button.removeEventListener('click', this.handleVariantSelectionBound);
    });

    this.productButtons.forEach((button) => {
      button.removeEventListener('click', this.handleProductSelectionBound);
    });

    if (this.standardAddToCartButton) {
      this.standardAddToCartButton.removeEventListener('click', this.handleSimpleAddToCartBound);
    }
  }

  handleProductSelection(event) {
    const selectedButton = event.currentTarget;
    const productHandle = selectedButton.dataset.productHandle;
    const blockId = selectedButton.dataset.blockId;

    console.log('[LazerVariantSelector] Product selected:', {
      productHandle,
      blockId,
    });

    // Update selected state
    this.productButtons.forEach((button) => button.classList.remove('selected'));
    selectedButton.classList.add('selected');

    // Find the selected product block
    const selectedBlock = this.products.find((block) => block.product === productHandle);
    if (!selectedBlock) return;

    // Get price elements
    const priceElement = selectedButton.querySelector('.product-price');
    const compareAtPriceElement = selectedButton.querySelector('.product-compare-price');

    // Update current prices
    const newPrice = this.getPriceAmount(priceElement.textContent);
    let newComparePrice = compareAtPriceElement ? this.getPriceAmount(compareAtPriceElement.textContent) : newPrice;

    // If no compare price element found, try getting it from the product data
    if (!compareAtPriceElement && selectedBlock.product_data?.variants?.[0]?.compare_at_price) {
      newComparePrice = selectedBlock.product_data.variants[0].compare_at_price;
    }

    this.currentVariantPrice = newPrice;
    this.currentVariantCompareAtPrice = newComparePrice;
    this.currentVariantSavings = Math.max(0, this.currentVariantCompareAtPrice - this.currentVariantPrice);

    // Update subscription variants visibility
    this.updateSubscriptionVariants(blockId, selectedBlock);

    // Select the first subscription variant for the new product
    this.selectFirstSubscriptionVariant(blockId);

    // Get the selected subscription variant
    const selectedSubscription = this.querySelector('.subscription-variant.selected');
    let subscriptionPrice = 0;

    if (selectedSubscription) {
      subscriptionPrice = parseInt(selectedSubscription.dataset.variantPrice, 10);

      // Apply subscription discount if BF deal is enabled and product allows it
      if (this.enableBfSubDeal && selectedBlock.enable_promo_sub_deal) {
        subscriptionPrice = Math.max(0, subscriptionPrice - this.subscriptionDiscountAmount);
      }
    }

    console.log('[LazerVariantSelector] Updating total price:', {
      productPrice: this.currentVariantPrice,
      subscriptionPrice,
      total: this.currentVariantPrice + subscriptionPrice,
    });

    // Update total price
    this.updateTotalPrice(subscriptionPrice);

    // Dispatch variant changed event
    this.dispatchVariantChangedEvent(selectedButton.dataset.variantId, subscriptionPrice);

    // Dispatch a custom event to notify that a product has been selected
    const productSelectedEvent = new CustomEvent('product-selected', {
      detail: {
        blockId: blockId,
        productHandle: productHandle,
        enableBfSubDeal: this.enableBfSubDeal,
        enablePromoSubDeal: selectedBlock.enable_promo_sub_deal,
        subscriptionDiscountAmount: this.subscriptionDiscountAmount,
      },
      bubbles: true,
    });
    document.dispatchEvent(productSelectedEvent);

    console.log('[LazerVariantSelector] Dispatched product-selected event:', {
      blockId: blockId,
      productHandle: productHandle,
      enableBfSubDeal: this.enableBfSubDeal,
      enablePromoSubDeal: selectedBlock.enable_promo_sub_deal,
    });
  }

  updateSubscriptionVariants(blockId, selectedBlock) {
    console.log('[LazerVariantSelector] Updating subscription variants for block:', blockId);

    // Hide all subscription variant lists
    const allLists = this.querySelectorAll('[class^="subscription-variants-"]');
    allLists.forEach((list) => {
      list.classList.add('tw-hidden');
    });

    // Show the selected block's subscription variants
    const activeList = this.querySelector(`.subscription-variants-${blockId}`);
    if (activeList) {
      activeList.classList.remove('tw-hidden');
      console.log('[LazerVariantSelector] Made subscription variants visible for block:', blockId);
    } else {
      console.log('[LazerVariantSelector] No subscription variants found for block:', blockId);
    }
  }

  getPriceAmount(priceString) {
    if (!priceString) return 0;

    // First, handle specific currency formats
    let cleanPrice = priceString;
    if (this.currency === 'CHF') {
      cleanPrice = priceString
        .replace('CHF', '')
        .replace(/[,]/g, '') // Remove all commas for CHF
        .trim();
    } else if (this.currency === 'AED' || priceString.includes('Dhs')) {
      cleanPrice = priceString.replace('Dhs.', '').replace(/[,]/g, '').trim();
    } else {
      // Handle other currencies
      cleanPrice = priceString
        .replace(/[£$€Dhs.\s]/g, '')
        .replace(/,(\d{2})$/, '.$1')
        .replace(/[,]/g, '');
    }

    const priceInCents = Math.round(parseFloat(cleanPrice) * 100);
    return priceInCents;
  }

  handleVariantSelection(event) {
    const selectedButton = event.currentTarget;
    const variantId = selectedButton.dataset.variantId;

    // Get the raw price from the dataset
    let variantPrice = parseInt(selectedButton.dataset.variantPrice, 10) || 0;

    // Apply subscription discount if BF deal is enabled
    if (this.enableBfSubDeal) {
      variantPrice = Math.max(0, variantPrice - this.subscriptionDiscountAmount);
    }

    this.updateSelectedState(selectedButton);
    this.updateTotalPrice(variantPrice);
    this.dispatchVariantChangedEvent(variantId, variantPrice);

    // Update dropdown trigger content
    this.updateDropdownTriggerContent(selectedButton);

    console.groupEnd();
    console.groupEnd();
  }

  updateSelectedState(selectedButton) {
    this.variantButtons.forEach((button) => button.classList.remove('selected'));
    selectedButton.classList.add('selected');
  }

  updateTotalPrice(subscriptionPrice) {
    const total = this.currentVariantPrice + (subscriptionPrice || 0);
    const totalWithSavings = this.currentVariantCompareAtPrice + (subscriptionPrice || 0);
    const totalSavings = Math.max(0, totalWithSavings - total);

    console.log('Price Update:', {
      currentVariantPrice: this.currentVariantPrice,
      subscriptionPrice,
      total,
      totalWithSavings,
      totalSavings,
    });

    this.totalPriceElements.forEach((element) => {
      element.textContent = this.formatPrice(total);
    });

    this.compareAtPriceElements.forEach((element) => {
      if (totalSavings > 0) {
        element.textContent = this.formatPrice(totalWithSavings);
        element.classList.remove('tw-hidden');
      } else {
        element.classList.add('tw-hidden');
      }
    });
  }

  dispatchVariantChangedEvent(variantId, variantPrice) {
    this.dispatchEvent(
      new CustomEvent('variantChanged', {
        detail: { variantId, variantPrice },
        bubbles: true,
      })
    );
  }

  async handleNormalCheckout(event) {
    event.preventDefault();
    await this.addToCartAndRedirect('/checkout');
  }

  async handleAlternativeCheckout(event) {
    event.preventDefault();
    await this.addToCartAndRedirect('/checkout?alternative_payment_methods=true');
  }

  async handleSimpleAddToCartBound(event) {
    event.preventDefault();
    console.log('handleSimpleAddToCartBound', this);
    const variantId = this.dataset.variantId;
    const quantity = this.dataset.quantity;
    const isSuit = this.dataset.isSuit === 'true';
    const isSubscription = this.dataset.productType === 'subscription';
    console.log('variantId', variantId);
    console.log('quantity', quantity);
    console.log('isSuit', isSuit);
    console.log('isSubscription', isSubscription);

    // Check current cart for suits/subscriptions if adding a suit or subscription
    if (isSuit || isSubscription) {
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();
      console.log('Current cart:', cart);

      const hasSuitOrSubscription = cart.items.some(
        (item) => item.properties?._is_suit === 'true' || item.properties?._product_type === 'subscription'
      );
      console.log('Has existing suit or subscription:', hasSuitOrSubscription);

      if (hasSuitOrSubscription) {
        console.log('Showing subscription dialog...');
        // Use slideout cart's subscription dialog
        const confirmed = await window.slideoutCart.showSubscriptionDialog();

        console.log('Dialog result:', confirmed);

        if (!confirmed) {
          console.log('User cancelled dialog');
          return;
        }

        console.log('Clearing cart...');
        // Clear cart if confirmed
        for (const item of cart.items) {
          await window.slideoutCart.updateQuantity(item.key, 0);
        }
      }
    }

    const properties = {};
    if (isSuit) {
      properties._is_suit = 'true';
    }
    if (isSubscription) {
      properties._product_type = 'subscription';
    }

    await window.slideoutCart.addToCart(variantId, quantity, properties);
  }

  async addToCartAndRedirect(checkoutUrl) {
    if (this.isAddingToCart) return;
    this.isAddingToCart = true;

    try {
      const selectedProduct = this.querySelector('[data-product-handle].selected');
      const selectedSubscription = this.querySelector('.subscription-variant.selected');
      const isAlternativePayment = checkoutUrl.includes('alternative_payment_methods=true');

      if (!selectedProduct) {
        console.error('No product selected');
        return;
      }

      const productHandle = selectedProduct.dataset.productHandle;
      const productVariantId = selectedProduct.dataset.variantId;
      const items = [];

      // Clear cart if needed - clear it every time
      await this.clearCart();
      // if (this.clearCartHandles.includes(productHandle)) {
      //   console.log('Clearing cart for product:', productHandle);
      // }

      // Add main product to cart
      items.push({
        id: productVariantId,
        quantity: 1,
      });

      // Add subscription if selected, using alternative variant ID for alternative payment methods
      if (selectedSubscription) {
        const subscriptionVariantId = isAlternativePayment
          ? selectedSubscription.dataset.variantIdAlternative
          : selectedSubscription.dataset.variantId;

        if (isAlternativePayment) {
          items.push({
            id: subscriptionVariantId,
            quantity: 1,
          });
        } else {
          items.push({
            id: subscriptionVariantId,
            quantity: 1,
            selling_plan: selectedSubscription.dataset.sellingPlanId,
          });
        }
      }

      await this.addItemsToCart(items);

      // Get the checkout URL from Shopify
      const response = await fetch('/cart.js');
      const cart = await response.json();

      // Construct locale string in correct format
      const shopifyLocale = Shopify?.locale || '';
      const locale = shopifyLocale.includes('-') ? shopifyLocale : 'en-US';
      const market = locale.split('-')[1] || 'US';

      // Build params with locale and market
      const params = new URLSearchParams({
        locale,
        market,
        ...(isAlternativePayment && { skip_shop_pay: 'true' }),
      });

      // Redirect to Shopify's checkout
      window.location.href = `/checkout?${params.toString()}`;
    } catch (error) {
      console.error('Error during checkout:', error);
    } finally {
      this.isAddingToCart = false;
    }
  }

  async addItemsToCart(items) {
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: items,
        }),
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error:', error);
      throw error;
    }
  }

  async clearCart() {
    try {
      console.log('Attempting to clear cart...');
      const response = await fetch('/cart/clear.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to clear cart');
      }

      const result = await response.json();
      console.log('Cart cleared successfully:', result);
      return result;
    } catch (error) {
      console.error('Error clearing cart:', error);
      throw error;
    }
  }

  formatPrice(cents) {
    try {
      const price = cents / 100;

      if (this.currency === '£') {
        // Special handling for GBP
        const formattedPrice = new Intl.NumberFormat('en-GB', {
          style: 'currency',
          currency: 'GBP',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(price);

        return formattedPrice.replace('GBP', '£');
      }

      // Handle other currencies
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: this.currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(price);
    } catch (error) {
      console.error('Price formatting error:', error);
      // Fallback formatting
      return `${this.currency}${Math.round(cents / 100)}`;
    }
  }

  // Add a new method to select the first subscription variant
  selectFirstSubscriptionVariant(blockId) {
    console.log('[LazerVariantSelector] Selecting first subscription variant for block:', blockId);

    // Find the subscription variants list for this block
    const subscriptionList = this.querySelector(`.subscription-variants-${blockId}`);
    if (!subscriptionList) {
      console.error('[LazerVariantSelector] No subscription list found for block:', blockId);
      return;
    }

    // Find the first subscription variant
    const firstVariant = subscriptionList.querySelector('.subscription-variant');
    if (!firstVariant) {
      console.error('[LazerVariantSelector] No subscription variants found');
      return;
    }

    // Clear any existing selected state
    const allVariants = this.querySelectorAll('.subscription-variant');
    allVariants.forEach((variant) => variant.classList.remove('selected'));

    // Select the first variant
    firstVariant.classList.add('selected');

    console.log('[LazerVariantSelector] Selected first subscription variant:', firstVariant.dataset.variantId);
  }
}

customElements.define('lazer-variant-selector', LazerVariantSelector);
